import React, { useState, useEffect } from 'react';
import { Mic, MicOff, Volume2, AlertCircle, CheckCircle } from 'lucide-react';
import voiceToTextService from '../services/voiceToTextService';
import webSpeechService from '../services/webSpeechService';
import transactionParser from '../services/transactionParser';
import { trackFeatureUsage } from '../firebase';

const detectIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return isIOS || isIPadOS;
};

const detectMobile = () => {
  if (typeof navigator === 'undefined') return false;
  if (navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean') {
    return navigator.userAgentData.mobile;
  }
  const ua = navigator.userAgent || '';
  return /android|iphone|ipad|ipod|mobile/i.test(ua);
};

const formatErrorForDebug = (error) => {
  if (!error) return '';
  const name = error.name || 'Error';
  const message = error.message || String(error);
  const cause = error.cause
    ? `${error.cause.name || 'Error'}: ${error.cause.message || String(error.cause)}`
    : '';
  return cause ? `${name}: ${message} | cause: ${cause}` : `${name}: ${message}`;
};

const isNetworkLikeSpeechError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  const name = String(error?.name || '').toLowerCase();
  return (
    name.includes('transcriptionunavailable') ||
    message.includes('network') ||
    message.includes("couldn't reach transcription") ||
    message.includes('failed to fetch')
  );
};

const VoiceInput = ({
  onTransactionParsed,
  onClose,
  className = "",
  debug = false,
  categories = {},
  defaultType = 'income',
  paymentMethods = []
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [parsedTransaction, setParsedTransaction] = useState(null);
  const [error, setError] = useState('');
  const [recordingTime, setRecordingTime] = useState(0);
  const [isSupported, setIsSupported] = useState(true);
  const [useWebSpeech, setUseWebSpeech] = useState(false);
  const [unsupportedReason, setUnsupportedReason] = useState('');
  const [supportNotice, setSupportNotice] = useState('');
  const [isIOS, setIsIOS] = useState(detectIOS);
  const [lastEngine, setLastEngine] = useState('');
  const [lastMimeType, setLastMimeType] = useState('');
  const [lastError, setLastError] = useState('');
  const showDebug = process.env.NODE_ENV !== 'production';
  const paymentOptions = (paymentMethods && paymentMethods.length)
    ? paymentMethods
    : [
        { id: 'cash', name: 'Cash' },
        { id: 'card', name: 'Card' },
        { id: 'transfer', name: 'Bank Transfer' },
        { id: 'revolut', name: 'Revolut' },
        { id: 'voucher', name: 'Gift Voucher' }
      ];

  useEffect(() => {
    const cloudSupported = voiceToTextService.isSupported();
    const webSupported = webSpeechService.isSupported();
    const isStandalone = (() => {
      if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
      if (typeof window.navigator !== 'undefined' && window.navigator.standalone) return true; // iOS
      return false;
    })();
    const isIOSDetected = detectIOS();
    const isMobile = detectMobile();
    setIsIOS(isIOSDetected);

    let notice = 'Voice input is available on desktop Safari and Chrome, and on mobile Chrome.';
    if (isIOSDetected || isMobile) {
      notice = 'On mobile we use cloud speech recognition for best reliability.';
    }

    const effectiveSupported = isIOSDetected ? cloudSupported : (cloudSupported || webSupported);
    if (!effectiveSupported) {
      notice = isIOSDetected
        ? 'Voice input is not supported in this browser on iOS.'
        : 'Voice input is not supported in this browser.';
      setIsSupported(false);
      setUnsupportedReason(notice);
    } else {
      setIsSupported(true);
      setUnsupportedReason('');
    }

    setSupportNotice(notice);
    
    // Debug categories
    console.log('VoiceInput: Categories received:', categories);
    console.log('VoiceInput: Categories.expenses length:', categories.expenses?.length);
    console.log('VoiceInput: Categories.income length:', categories.income?.length);
    
    // Prefer Web Speech only when not in standalone/PWA (it is blocked there on many devices).
    // On iOS: always use cloud speech-to-text for reliability.
    if (isIOSDetected || isMobile) {
      console.log('VoiceInput: mobile detected, using Cloud Speech-to-Text only');
      setUseWebSpeech(false);
    } else if (webSupported && !isStandalone) {
      console.log('VoiceInput: Using Web Speech API as primary');
      setUseWebSpeech(true);
    } else if (cloudSupported) {
      console.log('VoiceInput: Using Cloud Speech-to-Text (media recorder)');
      setUseWebSpeech(false);
    } else if (webSupported) {
      // Last resort: allow Web Speech even in standalone if nothing else works
      setUseWebSpeech(true);
    }
  }, [categories]);

  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  const showSpeechFallbackNotice = (message) => {
    setError('');
    setSupportNotice(message);
  };

  const startRecording = async () => {
    if (!isSupported) {
      const message = unsupportedReason || 'Voice input is not supported in this browser.';
      setError(message);
      setLastError(`Error: ${message}`);
      return;
    }

    const engine = useWebSpeech ? 'webSpeech' : 'cloud';
    const preferredMimeType = useWebSpeech ? '' : voiceToTextService.getPreferredMimeType();
    console.log(`VoiceInput: start voice engine=${engine} mimeType=${preferredMimeType || 'default'}`);

    try {
      if (useWebSpeech) {
        // Use Web Speech API (browser-based)
        console.log('VoiceInput: Starting Web Speech API recording');
        setLastEngine(engine);
        setLastMimeType(preferredMimeType);
        setLastError('');
        setError('');
        setTranscript('');
        setParsedTransaction(null);
        setIsRecording(true);
        setIsProcessing(false);
        
        const transcriptResult = await webSpeechService.recordAndTranscribe();
        console.log('VoiceInput: Received transcript:', transcriptResult);
        setTranscript(transcriptResult);
        
        // Parse the transcript
        console.log('VoiceInput: Parsing transcript...');
        console.log('VoiceInput: Exact transcript received:', JSON.stringify(transcriptResult));
        const parsed = transactionParser.parseTransaction(transcriptResult);
        parsed.type = defaultType || parsed.type;
        const cleanedDesc = cleanDescription(parsed.description || transcriptResult);
        parsed.description = cleanedDesc || parsed.description;
        console.log('VoiceInput: Parsed transaction:', JSON.stringify(parsed, null, 2));
        
        if (parsed.isSplitPayment) {
          console.log('VoiceInput: ✅ Split payment detected with', parsed.transactions.length, 'transactions');
        } else {
          console.log('VoiceInput: ❌ Single payment detected');
        }
        
        setParsedTransaction(parsed);
        
        setIsRecording(false);
        setIsProcessing(false);
        console.log('VoiceInput: Processing complete');
      } else {
        // Use Google Cloud Speech-to-Text
        await voiceToTextService.startRecording();
        const activeMimeType = voiceToTextService.getActiveMimeType() || preferredMimeType;
        setLastEngine(engine);
        setLastMimeType(activeMimeType);
        setLastError('');
        setError('');
        setTranscript('');
        setParsedTransaction(null);
        setIsRecording(true);
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('VoiceInput: Error in startRecording:', error);
      setLastEngine(engine);
      setLastMimeType(preferredMimeType);
      setLastError(formatErrorForDebug(error));
      setIsRecording(false);
      setIsProcessing(false);
      
      if (useWebSpeech && voiceToTextService.isSupported() && isNetworkLikeSpeechError(error)) {
        console.log('VoiceInput: Web Speech network error, switching to Cloud Speech for next try');
        setUseWebSpeech(false);
        showSpeechFallbackNotice('Browser speech recognition had a network error. Switched to cloud transcription - tap record again.');
      } else if (!useWebSpeech && !isIOS && webSpeechService.isSupported() && isNetworkLikeSpeechError(error)) {
        console.log('VoiceInput: Cloud failed, enabling Web Speech for next try');
        setUseWebSpeech(true);
        showSpeechFallbackNotice("Couldn't reach cloud transcription. Switched to browser speech recognition - tap record again.");
      } else {
        setError(error.message);
      }
    }
  };

  const stopRecording = async () => {
    if (!isRecording) return;

    setIsRecording(false);

    if (useWebSpeech) {
      setIsProcessing(true);
      // For Web Speech API, stopping will trigger the resolution in the service
      webSpeechService.stopRecording();
      // The transcript processing will happen in the startRecording promise resolution
      return;
    }

    try {
      setIsProcessing(true);
      const transcriptResult = await voiceToTextService.finishRecording();
      setTranscript(transcriptResult);
      
      // Parse the transcript
      const parsed = transactionParser.parseTransaction(transcriptResult);
      parsed.type = defaultType || parsed.type;
      const cleanedDesc = cleanDescription(parsed.description || transcriptResult);
      parsed.description = cleanedDesc || parsed.description;
      setParsedTransaction(parsed);
    } catch (error) {
      if (!useWebSpeech && !isIOS && webSpeechService.isSupported() && isNetworkLikeSpeechError(error)) {
        console.log('VoiceInput: Cloud transcription failed, enabling Web Speech for next try');
        setUseWebSpeech(true);
        showSpeechFallbackNotice("Couldn't reach cloud transcription. Switched to browser speech recognition - tap record again.");
      } else {
        setError(error.message || 'Failed to process voice input');
      }
      setLastError(formatErrorForDebug(error));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUseTransaction = () => {
    if (parsedTransaction && onTransactionParsed) {
      trackFeatureUsage('voice_input');
      console.log('VoiceInput: Adding transaction:', JSON.stringify(parsedTransaction, null, 2));
      console.log('VoiceInput: isSplitPayment?', parsedTransaction.isSplitPayment);

      const normalizeAmount = (value) => {
        if (value === '' || value === null || value === undefined) return 0;
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
      };
      
      if (parsedTransaction.isSplitPayment) {
        console.log('VoiceInput: Processing split transactions, count:', parsedTransaction.transactions.length);
        // Add each transaction separately
        parsedTransaction.transactions.forEach((transaction, index) => {
          const safeTransaction = { ...transaction, amount: normalizeAmount(transaction.amount) };
          console.log(`VoiceInput: Adding split transaction ${index + 1}:`, JSON.stringify(safeTransaction, null, 2));
          onTransactionParsed(safeTransaction);
        });
      } else {
        console.log('VoiceInput: Processing single transaction');
        const safeTransaction = { ...parsedTransaction, amount: normalizeAmount(parsedTransaction.amount) };
        onTransactionParsed(safeTransaction);
      }
      
      // Clear the state after adding transactions
      setTranscript('');
      setParsedTransaction(null);
      setError('');
      
      // Close the modal if onClose is provided
      if (onClose) {
        onClose();
      }
    }
  };



  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'text-green-600';
    if (confidence >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getConfidenceText = (confidence) => {
    if (confidence >= 80) return 'High confidence';
    if (confidence >= 60) return 'Medium confidence';
    return 'Low confidence - please review';
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-4 shadow-sm h-full flex flex-col ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Voice Input</h3>
        <div className="flex items-center gap-2">
          {/* Refresh button for mobile when stuck */}
          <button
            onClick={() => {
              // Reset voice input state without reloading page
              setIsRecording(false);
              setIsProcessing(false);
              setTranscript('');
              setParsedTransaction(null);
              setError('');
              setRecordingTime(0);
              console.log('Voice input refreshed');
            }}
            className="text-gray-500 hover:text-gray-700 p-1 rounded"
            title="Reset voice input"
          >
            🔄
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-1"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {supportNotice && (
        <div className="mb-4 text-sm px-3 py-2 rounded-lg border flex gap-2 items-start bg-blue-50 border-blue-200 text-blue-800">
          <AlertCircle size={16} className="mt-[2px]" />
          <span>{supportNotice}</span>
        </div>
      )}

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto">
      {/* Recording Controls */}
      <div className="flex flex-col items-center gap-4 mb-4">
        <div className="relative">
          <button
            onClick={isRecording ? stopRecording : startRecording}
            disabled={!isSupported || (isProcessing && !isRecording)}
            className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-200 shadow-lg ${
              isRecording
                ? 'bg-red-500 hover:bg-red-600 animate-pulse shadow-red-200'
                : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200'
            } ${isProcessing && !isRecording ? 'opacity-50' : ''} ${!isSupported ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {isProcessing ? (
              <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin" />
            ) : isRecording ? (
              <MicOff className="text-white" size={28} />
            ) : (
              <Mic className="text-white" size={28} />
            )}
          </button>
          
          {isRecording && (
            <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
              <span className="text-sm text-gray-600 font-mono">
                {formatTime(recordingTime)}
              </span>
            </div>
          )}
        </div>

        <div className="text-center">
          {isRecording ? (
            <div>
              <p className="text-lg text-red-600 font-semibold animate-pulse">
                Recording...
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Speak clearly and click microphone to stop
              </p>
            </div>
          ) : isProcessing ? (
            <div>
              <p className="text-lg text-indigo-600 font-semibold">
                Transcribing...
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Converting speech to text
              </p>
            </div>
          ) : !isSupported ? (
            <div>
              <p className="text-lg text-red-600 font-semibold">Microphone capture unavailable</p>
              <p className="text-sm text-gray-600 mt-1">
                Voice input works in desktop Safari/Chrome and mobile Chrome.
              </p>
            </div>
          ) : (
            <div>
              <p className="text-lg text-indigo-600 font-semibold">
                Click to Record
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Say: "haircut 40 euro" or "bought supplies 50"
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle size={16} />
            <span className="text-sm font-medium">Error</span>
          </div>
          <p className="text-red-600 text-sm mt-1">{error}</p>
        </div>
      )}
      {/* Transcript Display */}
      {transcript && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Volume2 size={16} className="text-gray-600" />
            <span className="text-sm font-medium text-gray-700">What you said:</span>
          </div>
          <p className="text-gray-800 italic">"{transcript}"</p>
        </div>
      )}

      {/* Parsed Transaction Display */}
      {parsedTransaction ? (
        <div className="mb-4">
          {parsedTransaction.isSplitPayment ? (
            // Split Payment Display
            <div className="space-y-3">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle size={16} className="text-purple-600" />
                  <span className="text-sm font-medium text-purple-700">
                    {parsedTransaction.transactions[0].splitCategoryInfo?.splitType === 'category' 
                      ? `Split Categories Detected (${parsedTransaction.transactions.length} transactions)`
                      : `Split Payment Detected (${parsedTransaction.transactions.length} transactions)`
                    }
                  </span>
                </div>
                <p className="text-xs text-purple-600">
                  Total: €{parsedTransaction.transactions.reduce((sum, t) => sum + (Number(t.amount) || 0), 0).toFixed(2)}
                </p>
              </div>
              
              {parsedTransaction.transactions.map((transaction, index) => (
                <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-green-700">Transaction {index + 1}</span>
                    <span className={`text-xs font-medium ${getConfidenceColor(transaction.confidence)}`}>
                      {getConfidenceText(transaction.confidence)}
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    {/* Editable Amount */}
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-600 w-16">Amount:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={transaction.amount}
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          const nextAmount = rawValue === '' ? '' : parseFloat(rawValue);
                          const updatedTransactions = [...parsedTransaction.transactions];
                          updatedTransactions[index] = {
                            ...updatedTransactions[index],
                            amount: Number.isNaN(nextAmount) ? '' : nextAmount
                          };
                          setParsedTransaction({
                            ...parsedTransaction,
                            transactions: updatedTransactions
                          });
                        }}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <span className="text-xs text-gray-500">€</span>
                    </div>

                    {/* Editable Date */}
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-600 w-16">Date:</label>
                      <input
                        type="date"
                        value={transaction.date || new Date().toISOString().split('T')[0]}
                        onChange={(e) => {
                          const updatedTransactions = [...parsedTransaction.transactions];
                          updatedTransactions[index] = {
                            ...updatedTransactions[index],
                            date: e.target.value
                          };
                          setParsedTransaction({
                            ...parsedTransaction,
                            transactions: updatedTransactions
                          });
                        }}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    {/* Editable Description */}
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-600 w-16">Item:</label>
                      <input
                        type="text"
                        value={transaction.description}
                        onChange={(e) => {
                          const updatedTransactions = [...parsedTransaction.transactions];
                          updatedTransactions[index] = {
                            ...updatedTransactions[index],
                            description: e.target.value
                          };
                          setParsedTransaction({
                            ...parsedTransaction,
                            transactions: updatedTransactions
                          });
                        }}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    {/* Category */}
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-600 w-16">Category:</label>
                      <select
                        value={transaction.category}
                        onChange={(e) => {
                          const updatedTransactions = [...parsedTransaction.transactions];
                          updatedTransactions[index] = {
                            ...updatedTransactions[index],
                            category: e.target.value
                          };
                          setParsedTransaction({
                            ...parsedTransaction,
                            transactions: updatedTransactions
                          });
                        }}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="">Select category</option>
                        {/* Show appropriate categories based on transaction type */}
                        {transaction.type === 'income' 
                          ? (categories.income || []).map(cat => (
                              <option key={cat.id} value={cat.id}>
                                {cat.icon} {cat.name}
                              </option>
                            ))
                          : (categories.expenses || []).map(cat => (
                              <option key={cat.id} value={cat.id}>
                                {cat.icon} {cat.name}
                              </option>
                            ))
                        }
                      </select>
                    </div>

                    {/* Payment Method */}
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-600 w-16">Payment:</label>
                      <select
                        value={transaction.paymentMethod}
                        onChange={(e) => {
                          const updatedTransactions = [...parsedTransaction.transactions];
                          updatedTransactions[index] = {
                            ...updatedTransactions[index],
                            paymentMethod: e.target.value
                          };
                          setParsedTransaction({
                            ...parsedTransaction,
                            transactions: updatedTransactions
                          });
                        }}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="">Select payment</option>
                        {paymentOptions.map((method) => (
                          <option key={method.id} value={method.id}>
                            {method.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* VAT Information - only show for expenses */}
                    {transaction.type === 'expenses' && (
                      <>
                        {/* VAT Amount */}
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-600 w-16">VAT:</label>
                          <input
                            type="number"
                            step="0.01"
                            value={transaction.vatAmount || ''}
                            onChange={(e) => {
                              const vatAmount = parseFloat(e.target.value) || null;
                              const updatedTransactions = [...parsedTransaction.transactions];
                              updatedTransactions[index] = {
                                ...updatedTransactions[index],
                                vatAmount: vatAmount
                              };
                              setParsedTransaction({
                                ...parsedTransaction,
                                transactions: updatedTransactions
                              });
                            }}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="VAT amount"
                          />
                          <span className="text-xs text-gray-500">€</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
              
              <div className="flex space-x-2">
                <button
                  onClick={handleUseTransaction}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Add All {parsedTransaction.transactions.length} Transactions
                </button>
                <button
                  onClick={() => {
                    setTranscript('');
                    setParsedTransaction(null);
                    setError('');
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
          ) : (
            // Single Transaction Display
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-blue-600" />
                  <span className="text-sm font-medium text-blue-700">Parsed Transaction</span>
                </div>
                <span className={`text-xs font-medium ${getConfidenceColor(parsedTransaction.confidence)}`}>
                  {getConfidenceText(parsedTransaction.confidence)}
                </span>
              </div>

              <div className="space-y-3">
                {/* Type (editable) */}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-600 w-20">Type:</label>
                  <select
                    value={parsedTransaction.type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setParsedTransaction({
                        ...parsedTransaction,
                        type: newType,
                        // clear category when switching type to avoid showing wrong list
                        category: ''
                      });
                    }}
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 capitalize"
                  >
                    <option value="income">Sale</option>
                    <option value="expenses">Expense</option>
                  </select>
                </div>

                {/* Editable Date */}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-600 w-20">Date:</label>
                  <input
                    type="date"
                    value={parsedTransaction.date || new Date().toISOString().split('T')[0]}
                    onChange={(e) => {
                      setParsedTransaction({
                        ...parsedTransaction,
                        date: e.target.value
                      });
                    }}
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                {/* Editable Amount */}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-600 w-20">Amount:</label>
                  <input
                    type="number"
                    step="0.01"
                    value={parsedTransaction.amount}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      const nextAmount = rawValue === '' ? '' : parseFloat(rawValue);
                      setParsedTransaction({
                        ...parsedTransaction,
                        amount: Number.isNaN(nextAmount) ? '' : nextAmount
                      });
                    }}
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-500">€</span>
                </div>

                {/* Editable Description */}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-600 w-20">Description:</label>
                  <input
                    type="text"
                    value={parsedTransaction.description}
                    onChange={(e) => {
                      setParsedTransaction({
                        ...parsedTransaction,
                        description: e.target.value
                      });
                    }}
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                {/* Editable Category */}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-600 w-20">Category:</label>
                  <select
                    value={parsedTransaction.category}
                    onChange={(e) => {
                      setParsedTransaction({
                        ...parsedTransaction,
                        category: e.target.value
                      });
                    }}
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select category</option>
                    {parsedTransaction.type === 'income' 
                      ? (categories.income || []).map(cat => (
                          <option key={cat.id} value={cat.id}>
                            {cat.icon} {cat.name}
                          </option>
                        ))
                      : (categories.expenses || []).map(cat => (
                          <option key={cat.id} value={cat.id}>
                            {cat.icon} {cat.name}
                          </option>
                        ))
                    }
                  </select>
                </div>

                {/* Payment Method (read-only for now) */}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-600 w-20">Payment:</label>
                  <select
                    value={parsedTransaction.paymentMethod}
                    onChange={(e) => {
                      setParsedTransaction({
                        ...parsedTransaction,
                        paymentMethod: e.target.value
                      });
                    }}
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 capitalize"
                  >
                    <option value="">Select payment</option>
                    {paymentOptions.map((method) => (
                      <option key={method.id} value={method.id}>
                        {method.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* VAT Information - only show for expenses */}
                {parsedTransaction.type === 'expenses' && (
                  <>
                    {/* Net Amount */}
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-600 w-20">Net Amount:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={parsedTransaction.netAmount || ''}
                        onChange={(e) => {
                          const netAmount = parseFloat(e.target.value) || null;
                          const vatAmount = parsedTransaction.vatAmount || 0;
                          setParsedTransaction({
                            ...parsedTransaction,
                            netAmount: netAmount,
                            amount: netAmount ? netAmount + vatAmount : parsedTransaction.amount
                          });
                        }}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Net amount (optional)"
                      />
                      <span className="text-xs text-gray-500">€</span>
                    </div>

                    {/* VAT Amount */}
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-600 w-20">VAT Amount:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={parsedTransaction.vatAmount || ''}
                        onChange={(e) => {
                          const vatAmount = parseFloat(e.target.value) || null;
                          const netAmount = parsedTransaction.netAmount || 0;
                          setParsedTransaction({
                            ...parsedTransaction,
                            vatAmount: vatAmount,
                            amount: netAmount ? netAmount + (vatAmount || 0) : parsedTransaction.amount,
                            vatRate: (netAmount && vatAmount) ? vatAmount / netAmount : parsedTransaction.vatRate
                          });
                        }}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="VAT amount (optional)"
                      />
                      <span className="text-xs text-gray-500">€</span>
                    </div>

                    {/* VAT Rate (calculated or manual) */}
                    {parsedTransaction.vatRate && (
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-600 w-20">VAT Rate:</label>
                        <span className="flex-1 px-2 py-1 text-sm bg-gray-100 rounded">
                          {(parsedTransaction.vatRate * 100).toFixed(1)}%
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {parsedTransaction.confidence < 80 && (
                <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-yellow-700 text-xs">
                    ⚠️ Please review the parsed information before saving. 
                    You can edit the details in the transaction form.
                  </p>
                </div>
              )}

              {/* Mobile-friendly action buttons - always visible */}
              <div className="sticky bottom-0 bg-white pt-4 mt-4 border-t border-gray-200">
                <div className="flex space-x-2">
                  <button
                    onClick={handleUseTransaction}
                    className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                  >
                    ✅ Use Transaction
                  </button>
                  <button
                    onClick={() => {
                      setTranscript('');
                      setParsedTransaction(null);
                      setError('');
                    }}
                    className="px-4 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium text-sm"
                  >
                    🗑️ Clear
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="mb-4 border border-dashed border-gray-200 rounded-lg bg-gray-50 p-3 text-sm text-gray-600">
          {unsupportedReason
            ? unsupportedReason
            : 'Record a transaction to see the parsed details here.'}
        </div>
      )}
      </div>

      {/* Debug Information */}
      {showDebug && (
        <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded mb-4">
          <p><strong>Debug Info:</strong></p>
          <p>• Google Cloud supported: {voiceToTextService.isSupported() ? 'Yes' : 'No'}</p>
          <p>• Web Speech supported: {webSpeechService.isSupported() ? 'Yes' : 'No'}</p>
          <p>• Currently using: {useWebSpeech ? 'Web Speech API' : 'Google Cloud'}</p>
          <p>• Engine: {lastEngine || 'n/a'}</p>
          <p>• Mime type: {lastMimeType || 'n/a'}</p>
          {lastError && <p>• Last error: {lastError}</p>}
          
          <div className="mt-2">
            <button 
              onClick={async () => {
                try {
                  console.log('Testing Web Speech API directly...');
                  const result = await webSpeechService.recordAndTranscribe();
                  console.log('Direct test result:', result);
                  setTranscript(result);
                  const parsed = transactionParser.parseTransaction(result);
                  setParsedTransaction(parsed);
                } catch (error) {
                  console.error('Direct test failed:', error);
                  setError(error.message);
                }
              }}
              className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
            >
              Test Web Speech
            </button>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="text-xs text-gray-500 space-y-1">
        <p><strong>Examples:</strong></p>
        <p>• "Sale acrylic nails €50 paid in cash"</p>
        <p>• "Purchase hair dryer and clips from salon supplies €300 paid by card"</p>
        <p>• "Facial treatment €85 paid by Revolut"</p>
      </div>
    </div>
  );
};

export default VoiceInput;

// Clean up spoken text so the description only contains the item/service name
const cleanDescription = (raw) => {
  if (!raw) return '';
  let desc = raw;

  const isModelNumber = (text, offset, numberText) => {
    const numericValue = Number(numberText);
    if (!Number.isInteger(numericValue) || numericValue < 2 || numericValue > 100) {
      return false;
    }

    const before = text.slice(Math.max(0, offset - 80), offset).replace(/\s+/g, ' ').trim();
    if (!before) return false;

    const productPattern = '(?:iphone|ipad|macbook|apple\\s+watch|airpods|samsung(?:\\s+galaxy)?|galaxy|google\\s+pixel|pixel|phone|mobile\\s+phone|smartphone|tablet|laptop|computer|printer|camera|monitor|tv)';
    const modelWordPattern = '(?:pro|max|mini|plus|ultra|se|air|series|galaxy|note|edge|fold|flip)';
    const modelPrefixPattern = new RegExp(`\\b${productPattern}(?:\\s+(?:\\d+[a-z]*|${modelWordPattern}))*\\s*$`, 'i');
    return modelPrefixPattern.test(before);
  };

  // Strip currency/amount patterns
  desc = desc
    .replace(/[\u20ac\u00a3$]\s*[\d.,]+/g, '')
    .replace(/\b\d+(\.\d+)?\s*(euro|eur|\u20ac)?\b/gi, (match, _decimalPart, currency, offset, fullText) => {
      const numberMatch = match.match(/\d+(\.\d+)?/);
      const numberText = numberMatch ? numberMatch[0] : '';
      if (!currency && isModelNumber(fullText, offset, numberText)) {
        return match;
      }
      return '';
    });
  // Remove trailing payment phrasing (e.g., "paid by card", "paid with cash")
  desc = desc.replace(/\bpaid\s+(by|with|via)\s+[a-z0-9\s]+$/i, '');
  // Remove standalone payment keywords
  desc = desc.replace(/\b(paid|pay|cash|card|credit|debit|revolut)\b/gi, '');
  // Collapse extra spaces
  desc = desc.replace(/\s{2,}/g, ' ').trim();
  return desc;
};

